import React, { useState } from 'react';
import { Col, Container, ProgressBar, Row } from 'react-bootstrap';
import Image from 'next/image';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { getProductReviews } from 'aster-foundation/core/epharmacy/RatingsAndReviews/redux/actions';
import { REDIRECT_URI } from 'aster-foundation/core/common/utils/ApiConst';
import { IC_STAR_FILLED_GREY, IC_WRITE_REVIEW } from '../../../../public/images';
import BlankCard from '@common/Component/BlankCard/BlankCard';
import StarRatingNumberOnly from '@common/Component/StarRatingNumberOnly/StarRatingNumberOnly';
import { BreadcrumbControl, ButtonBase } from '@common/Component/Forms';
import { FLAGSMITH_KEYS, HOME, ONLINE_PHARMACY_ROUTE, ONLINE_PHARMACY_TITLE } from '@common/Utility/constants';
import HeaderBelow from '@common/Component/HeaderBelow/HeaderBelow';
import Loader from '@common/Component/Loader/Loader';
import AddProductReviews from './AddProductReviews';
import CustomModal from '@common/Component/Modal/Modal';
import { dataLayerFromGuestToLogin } from '@common/Component/BookAppointment/BookAppointmentAnalytics';
import { sendEventToSmartLook } from '@common/Utility/smartlook';
import { sendLoginEvent } from '@common/Utility/analytics-utils';
import DisplayProductReviews from './DisplayProductReviews';
import logger from '@common/Utility/logger';
import { useAuth } from '@common/AuthService';

import ProductReviewsStyled from './ProductReviewsStyled';
import { errorHandlingEpharmacy, getReferralPageForGA } from '@common/Utility/common-utils';
import { useNavigationURL } from '@common/Utility/generateNavigationURL';
import { flagsmith } from '@common/Lib/Flagsmith';

const showLimit = 2;

const ProductReviews = (props) => {
  const { columnView, detailsData } = props;
  const skuId = detailsData?.sku;
  const productName = detailsData?.name;

  const router = useRouter();
  const dispatch = useDispatch();
  const { authCtx } = useAuth();
  const generateURL = useNavigationURL();
  const userLogin = useSelector((state) => state?.astercore?.checkUserExist?.data?.entry[0]?.resource);

  const [showRating, setShowRating] = useState(false);
  const [reviewsData, setReviewsData] = useState([]);
  const [ratings, setRatings] = useState([0, 0, 0, 0, 0]);
  const [showLoginCTA, setShowLoginCTA] = useState(false);
  const [productReviewsList, setProductReviewsList] = useState({});
  const [editReviewData, setEditReviewData] = useState([]);
  const [reviewsCount, setReviewsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const pageType = getReferralPageForGA(router?.asPath);
  const screenName = router?.pathname;
  const web_feature_flaging = flagsmith.getValue(FLAGSMITH_KEYS.WEB_FEATURE_FLAGING, {json: true});

  React.useEffect(() => {
    if (skuId) {
      dispatch(getProductReviews({
        params: { skuId, userPatientId: userLogin?.id, writeReview: false },
        onSuccess: (response) => {
          setProductReviewsList(response ?? {});
          if (response && response.reviews) {
            setReviewsCount(response.reviews.length);
          }
          setIsLoading(false);
        },
        onFailure: (error) => {
          logger.error('Get All Reviews API Error', error);
          setIsLoading(false);
          errorHandlingEpharmacy(error, true, screenName, pageType);
        }
      }));
    }
  }, [skuId, userLogin]);

  const onWriteReview = () => {
    if (userLogin) {
      dispatch(getProductReviews({
        params: { skuId, userPatientId: userLogin?.id, writeReview: true },
        onSuccess: (response) => {
          if (response?.reviewAdded && response?.reviews?.length > 0) {
            setEditReviewData(response?.reviews);
          }
        },
        onFailure: (error) => {
          errorHandlingEpharmacy(error, true, screenName, pageType);
          logger.error('Edit Review API Error', error);
        }
      }));
      setShowRating(true);
    } else {
      setShowLoginCTA(true);
    }
  };

  const LoginToOpen = () => {
    dataLayerFromGuestToLogin('Login_From_Guest_Mode', 'Product details Page');
    const properties = {
      type: 'Product details Page',
    };
    sendEventToSmartLook('login_from_guest_mode', properties);
    sendLoginEvent(router?.asPath);
    authCtx.login({
      redirectUri: `${REDIRECT_URI}${generateURL(`?asPath=${encodeURIComponent(router.asPath)}`)}`,
    });
  };

  const LoginToCancelButton = () => {
    setShowLoginCTA(false);
  };

  const totalReviewCount = () => productReviewsList?.aggregateRating?.reviewCount;

  React.useEffect(() => {
    if (!columnView && productReviewsList?.reviews?.length > showLimit) {
      setReviewsData(productReviewsList?.reviews?.slice(0, showLimit));
    } else {
      setReviewsData(productReviewsList?.reviews);
    }
  }, [productReviewsList?.reviews?.length, columnView]);

  React.useEffect(() => {
    const ratingsArray = [];
    if (productReviewsList && productReviewsList?.aggregateRating && productReviewsList?.aggregateRating?.ratingsPercent) {
      Object.keys(productReviewsList?.aggregateRating?.ratingsPercent).map((item) => {
        if (item.includes('star')) {
          ratingsArray.push(productReviewsList?.aggregateRating?.ratingsPercent[item]);
        }
      });
    }
    if (ratingsArray.length > 0) {
      setRatings(ratingsArray);
    }
  }, [ratings.length, productReviewsList, skuId]);

  return (
    <ProductReviewsStyled>
      <div className={` ${columnView ? 'pageContent pb-0' : ''}`}>
        {columnView && (
          <>
            <Loader isLoading={isLoading} />
            <HeaderBelow />
            <Container>
              <Row className="py-3">
                {productName && (
                  <BreadcrumbControl
                    appVariant="epharmacy"
                    main="Customer reviews"
                    childs={
                      web_feature_flaging?.isBreadcrumbAEDisabled
                      ? [{ text: 'Home', link: generateURL(ONLINE_PHARMACY_ROUTE) }, { text: productName, link: generateURL(`${ONLINE_PHARMACY_ROUTE}/p/${productName}/${skuId}`) }]
                      : [{ text: 'Home', link: generateURL(HOME) }, { text: ONLINE_PHARMACY_TITLE, link: generateURL(ONLINE_PHARMACY_ROUTE) }, { text: productName, link: generateURL(`${ONLINE_PHARMACY_ROUTE}/p/${productName}/${skuId}`) }]
                    }
                  />
                )}
              </Row>
            </Container>
          </>
        )}
        <Container>
          <Row>
            <Col className='p-0'>
              <BlankCard
                customClass={columnView ? 'ratingAndReviewBox columnView' : 'ratingAndReviewBox'}
                title="Ratings & Review"
                border
                rightData={
                  <a className="writeReview" onClick={() => onWriteReview()}>
                    <Image src={IC_WRITE_REVIEW.src} width={17} height={16} alt="review Icon" />{' '}
                    <div>Write a Review</div>
                  </a>
                }
              >
                <div className="contentWrapper">
                  <div className="ratingsWrapper">
                    <div className="ratingAverage">
                      <div className="ratingAverageWrapper">
                        <StarRatingNumberOnly
                          className="onRatingBig"
                          ratings={reviewsCount > 0 ? (parseFloat(productReviewsList?.aggregateRating?.avgRating).toFixed(1)) : '0.0'}
                          iconHeight='20'
                          iconWidth='20'
                          imageSrc={reviewsCount <= 0 && IC_STAR_FILLED_GREY}
                        />
                        {reviewsCount > 0 && (
                          <div className="reviewCounts">
                            {totalReviewCount()} {totalReviewCount() === 1 ? 'Review' : 'Reviews'}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ratingsDivision">
                      {ratings &&
                        ratings.length > 0 &&
                        ratings.map((item, index) => (
                          <div className="ratingPercentage" key={ratings.length - Number(index)}>
                            <StarRatingNumberOnly
                              className="onRatingSmall"
                              ratings={ratings.length - Number(index)}
                              imageSrc={item === 0 && IC_STAR_FILLED_GREY}
                            />
                            <ProgressBar now={item} />
                            <div className="progressValue">{Math.round(item)}%</div>
                          </div>
                        ))}
                      {reviewsCount > 0 && (
                        <a className="writeReview" onClick={() => onWriteReview()}>
                          <Image src={IC_WRITE_REVIEW.src} width={17} height={16} alt="review Icon" />
                          <div>Write a Review</div>
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="reviewsWrapper">
                    <div className="reviewTitle">
                      <div>
                        {reviewsCount > 0
                          ? `Customer Reviews (${productReviewsList?.aggregateRating?.reviewCount})`
                          : 'No Customer Reviews'}
                      </div>
                      {!columnView && reviewsCount > 0 && (
                        <div>
                          <a
                            className="viewAllLink"
                            onClick={() => router.push({ pathname: generateURL(`/online-pharmacy/reviews/product/${skuId}`) })}
                          >
                            View All Reviews
                          </a>
                        </div>
                      )}
                    </div>
                    <div className="commentList">
                      {reviewsData && reviewsData.length > 0 ? (
                        reviewsData.map((reviews) => <DisplayProductReviews data={reviews} key={reviews?.id} columnView={columnView} />)
                      ) : (
                        <ButtonBase
                          customClass="noReviewButton"
                          data-test="AddToCartButton"
                          iconPosition="left"
                          variant="outline-success"
                          text="Be the first one to write a review"
                          onClick={() => onWriteReview()}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </BlankCard>
            </Col>
          </Row>
        </Container>
        {showRating && (
          <AddProductReviews
            setShowRating={setShowRating}
            detailsData={detailsData}
            userData={userLogin}
            editReviewData={editReviewData}
          />
        )}
      </div>
      {showLoginCTA && (
        <CustomModal
          bodyTextCenter
          footerBtnFullWidth
          modalTitle="Login/Register"
          size="md"
          footerAlignment="center"
          primaryButtonText="Login/Register"
          secondaryButtonText="Cancel"
          primaryButtonClicked={LoginToOpen}
          seconderyButtonClcked={LoginToCancelButton}
        >
          <p className="mb-0 font-17">Please Login/Register first to access this section</p>
        </CustomModal>
      )}
    </ProductReviewsStyled>
  );
};

ProductReviews.propTypes = {
  columnView: PropTypes.bool,
  detailsData: PropTypes.object
};

ProductReviews.defaultProps = {
  columnView: false,
  detailsData: {}
};

export default ProductReviews;
